import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useMaintenanceStore } from '@/stores/maintenanceStore'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import(/* webpackChunkName: "home" */ '../views/HomeView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/city',
      name: 'city',
      component: () => import(/* webpackChunkName: "city" */ '../views/CitySelectionView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import(/* webpackChunkName: "auth" */ '../views/auth/LoginView.vue'),
      meta: { requiresGuest: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import(/* webpackChunkName: "auth" */ '../views/auth/RegisterView.vue'),
      meta: { requiresGuest: true },
    },
    {
      path: '/verify-code',
      name: 'verify-code',
      component: () => import(/* webpackChunkName: "auth" */ '../views/auth/VerifyCodeView.vue'),
      meta: { requiresGuest: true },
    },
    {
      path: '/profile/:orgId',
      name: 'profile',
      component: () => import(/* webpackChunkName: "profile" */ '../views/ProfileView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/profile',
      name: 'my-profile',
      component: () => import(/* webpackChunkName: "profile" */ '../views/ProfileView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import(/* webpackChunkName: "settings" */ '../views/SettingsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/privacy-settings',
      name: 'privacy-settings',
      component: () => import(/* webpackChunkName: "privacy" */ '../views/PrivacySettingsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/resource-centers',
      name: 'resource-centers',
      component: () => import(/* webpackChunkName: "resource-centers" */ '../views/ResourcesView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/resources',
      redirect: '/resource-centers',
    },
    {
      path: '/resource-centers/:centerId',
      name: 'resource-center-detail',
      component: () => import(/* webpackChunkName: "resource-center-detail" */ '../views/ResourceCenterDetailView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/resource-centers/:centerId/:resourceId',
      name: 'resource-detail',
      component: () => import(/* webpackChunkName: "resource-detail" */ '../views/ResourceDetailView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/my-bookings',
      name: 'my-bookings',
      component: () => import(/* webpackChunkName: "my-bookings" */ '../views/MyBookingsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/resource-centers/book/:resourceId?',
      name: 'resource-booking',
      component: () => import(/* webpackChunkName: "resource-booking" */ '../views/ResourceBookingView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/posts',
      name: 'posts',
      component: () => import(/* webpackChunkName: "posts" */ '../views/PostsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/posts/create',
      name: 'create-post',
      component: () => import(/* webpackChunkName: "create-post" */ '../views/CreatePostView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/posts/:documentId/edit',
      name: 'edit-post',
      component: () => import(/* webpackChunkName: "create-post" */ '../views/CreatePostView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/posts/:slug',
      name: 'post-detail',
      component: () => import(/* webpackChunkName: "post-detail" */ '../views/PostDetailView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/organizations',
      name: 'organizations',
      component: () => import(/* webpackChunkName: "organizations" */ '../views/OrganizationsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/events',
      name: 'events',
      component: () => import(/* webpackChunkName: "events" */ '../views/EventsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/events/create',
      name: 'create-event',
      component: () => import(/* webpackChunkName: "create-event" */ '../views/CreateEventView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/events/:documentId/edit',
      name: 'edit-event',
      component: () => import(/* webpackChunkName: "create-event" */ '../views/CreateEventView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/events/:slug',
      name: 'event-detail',
      component: () => import(/* webpackChunkName: "event-detail" */ '../views/EventDetailView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/webinars',
      name: 'webinars',
      component: () => import(/* webpackChunkName: "webinars" */ '../views/WebinarsListingPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/webinars/create',
      name: 'create-webinar',
      component: () => import(/* webpackChunkName: "create-webinar" */ '../views/CreateWebinarView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/webinars/:documentId/edit',
      name: 'edit-webinar',
      component: () => import(/* webpackChunkName: "create-webinar" */ '../views/CreateWebinarView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/webinars/:slug',
      name: 'webinar-detail',
      component: () => import(/* webpackChunkName: "webinar-detail" */ '../views/WebinarDetailView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/news',
      name: 'news',
      component: () => import(/* webpackChunkName: "news" */ '../views/NewsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/news/create',
      name: 'create-news',
      component: () => import(/* webpackChunkName: "create-news" */ '../views/CreateNewsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/news/:slug',
      name: 'news-detail',
      component: () => import(/* webpackChunkName: "news-detail" */ '../views/NewsDetailView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/services',
      name: 'services',
      component: () => import(/* webpackChunkName: "services" */ '../views/ServicesView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/services/create',
      name: 'create-service',
      component: () => import(/* webpackChunkName: "create-service" */ '../views/CreateServiceView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/services/:documentId/edit',
      name: 'edit-service',
      component: () => import(/* webpackChunkName: "create-service" */ '../views/CreateServiceView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/faqs',
      name: 'faqs',
      component: () => import(/* webpackChunkName: "faqs" */ '../views/FaqsView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/volunteering',
      name: 'volunteering',
      component: () => import(/* webpackChunkName: "volunteering" */ '../views/VolunteeringView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/volunteering/create',
      name: 'create-volunteering',
      component: () => import(/* webpackChunkName: "create-volunteering" */ '../views/CreateVolunteeringView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/volunteering/:documentId/edit',
      name: 'edit-volunteering',
      component: () => import(/* webpackChunkName: "create-volunteering" */ '../views/CreateVolunteeringView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/volunteering/:documentId',
      name: 'volunteering-detail',
      component: () => import(/* webpackChunkName: "volunteering-detail" */ '../views/VolunteeringDetailView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    {
      path: '/opportunities',
      name: 'opportunities',
      component: () => import(/* webpackChunkName: "opportunities" */ '../views/OpportunitiesView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/opportunities/:documentId',
      name: 'opportunity-detail',
      component: () => import(/* webpackChunkName: "opportunity-detail" */ '../views/OpportunityDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/messages',
      name: 'messages',
      component: () => import(/* webpackChunkName: "messages" */ '../views/MessagesView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/messages/:conversationId',
      name: 'message-conversation',
      component: () => import(/* webpackChunkName: "messages" */ '../views/MessagesView.vue'),
      props: true,
      meta: { requiresAuth: true },
    },
    // {
    //   path: '/services/:documentId',
    //   name: 'service-detail',
    //   component: () => import(/* webpackChunkName: "service-detail" */ '../views/ServiceDetailView.vue'),
    //   props: true,
    //   meta: { requiresAuth: true },
    // },
    {
      path: '/dashboard',
      component: () => import(/* webpackChunkName: "dashboard" */ '../layouts/DashboardLayout.vue'),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      meta: { requiresAuth: true, requiresRole: ['content-manager', 'admin-manager'] as any },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import(/* webpackChunkName: "dashboard" */ '../views/dashboard/DashboardIndex.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'posts',
          name: 'dashboard-posts',
          component: () => import(/* webpackChunkName: "dashboard-posts" */ '../views/dashboard/PostsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'bookings',
          name: 'dashboard-bookings',
          component: () => import(/* webpackChunkName: "dashboard-bookings" */ '../views/dashboard/BookingsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'organizations',
          name: 'dashboard-organizations',
          component: () => import(/* webpackChunkName: "dashboard-organizations" */ '../views/dashboard/OrganizationsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'resources',
          name: 'dashboard-resources',
          component: () => import(/* webpackChunkName: "dashboard-resources" */ '../views/dashboard/ResourcesPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'events',
          name: 'dashboard-events',
          component: () => import(/* webpackChunkName: "dashboard-events" */ '../views/dashboard/EventsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'webinars',
          name: 'dashboard-webinars',
          component: () => import(/* webpackChunkName: "dashboard-webinars" */ '../views/dashboard/WebinarsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'news',
          name: 'dashboard-news',
          component: () => import(/* webpackChunkName: "dashboard-news" */ '../views/dashboard/NewsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'services',
          name: 'dashboard-services',
          component: () => import(/* webpackChunkName: "dashboard-services" */ '../views/dashboard/ServicesPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'categories',
          name: 'dashboard-categories',
          component: () => import(/* webpackChunkName: "dashboard-categories" */ '../views/dashboard/CategoriesPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'chats',
          name: 'dashboard-chats',
          component: () => import(/* webpackChunkName: "dashboard-chats" */ '../views/dashboard/ChatsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'chats/:conversationId',
          name: 'dashboard-chat-conversation',
          component: () => import(/* webpackChunkName: "dashboard-chats" */ '../views/dashboard/ChatsPage.vue'),
          props: true,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'analytics',
          name: 'dashboard-analytics',
          component: () => import(/* webpackChunkName: "dashboard-analytics" */ '../views/dashboard/AnalyticsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'reports',
          name: 'dashboard-reports',
          component: () => import(/* webpackChunkName: "dashboard-reports" */ '../views/dashboard/ReportsPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
        {
          path: 'problem-reports',
          name: 'dashboard-problem-reports',
          component: () => import(/* webpackChunkName: "dashboard-problem-reports" */ '../views/dashboard/ProblemReportsPage.vue'),
          meta: { requiresRole: 'admin-manager' },
        },
        {
          path: 'deletion-requests',
          name: 'dashboard-deletion-requests',
          component: () => import(/* webpackChunkName: "dashboard-deletion-requests" */ '../views/dashboard/DeletionRequestsPage.vue'),
          meta: { requiresRole: 'admin-manager' },
        },
        {
          path: 'resource-centers',
          name: 'dashboard-resource-centers',
          component: () => import(/* webpackChunkName: "dashboard-resource-centers" */ '../views/dashboard/ResourceCentersPage.vue'),
          meta: { requiresRole: 'admin-manager' },
        },
        {
          path: 'resource-center-edit',
          name: 'dashboard-resource-center-edit',
          component: () => import(/* webpackChunkName: "dashboard-resource-center-edit" */ '../views/dashboard/ResourceCenterEditPage.vue'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          meta: { requiresRole: ['content-manager', 'admin-manager'] as any },
        },
      ],
    },
    {
      path: '/linataawan-snapshot',
      name: 'linataawan-snapshot',
      component: () => import(/* webpackChunkName: "snapshot" */ '../views/LinataawanSnapshotView.vue'),
    },
    {
      path: '/private-sector-mapping',
      name: 'private-sector-mapping',
      component: () => import(/* webpackChunkName: "snapshot" */ '../views/PrivateSectorMappingView.vue'),
    },
    {
      path: '/ngo-municipality-toolkit',
      name: 'ngo-municipality-toolkit',
      component: () => import(/* webpackChunkName: "toolkit" */ '../views/NgoMunicipalityToolkitView.vue'),
    },
    {
      path: '/terms-and-conditions',
      name: 'terms-and-conditions',
      component: () => import(/* webpackChunkName: "legal" */ '../views/TermsAndConditionsView.vue'),
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: () => import(/* webpackChunkName: "legal" */ '../views/PrivacyPolicyView.vue'),
    },
    {
      path: '/about',
      name: 'about',
      component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue'),
    },
    {
      path: '/request-data-delete',
      name: 'request-data-delete',
      component: () => import(/* webpackChunkName: "data-delete" */ '../views/RequestDataDeleteView.vue'),
    },
    {
      path: '/report-problem',
      name: 'report-problem',
      component: () => import(/* webpackChunkName: "accessibility" */ '../views/ProblemReportView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import(/* webpackChunkName: "error" */ '../views/NotFoundView.vue'),
    },
  ],
})

// Navigation Guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const maintenanceStore = useMaintenanceStore()

  // Check maintenance mode first (blocks all navigation)
  const isMaintenanceMode = await maintenanceStore.checkMaintenanceMode()
  if (isMaintenanceMode) {
    // Block all navigation during maintenance mode
    // The MaintenancePage will be shown by App.vue
    return
  }

  // Handle loading state during auth initialization for all routes
  if (authStore.isLoading) {
    // Wait for auth to finish loading
    await new Promise(resolve => {
      const unwatch = authStore.$subscribe((mutation, state) => {
        if (!state.isLoading) {
          unwatch()
          resolve(undefined)
        }
      })
    })
  }

  // On a fresh page load the guard can run before the app has loaded the user.
  // With a session cookie present, load the user first instead of treating them as logged out.
  if (!authStore.isLoggedIn && authStore.hasValidToken) {
    await authStore.initializeAuth()
  }

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // Check if user is authenticated
    if (!authStore.isLoggedIn) {
      // Store intended destination for redirect after login
      const redirectTo = to.fullPath !== '/login' ? to.fullPath : '/dashboard'
      next({
        name: 'login',
        query: { redirect: redirectTo }
      })
      return
    }

    // Check role requirements. Every matched record must allow the role, so a child page
    // can be stricter than its parent (e.g. admin-only pages inside /dashboard).
    const roleAllowed = (requiredRole: unknown): boolean => {
      if (Array.isArray(requiredRole)) {
        // Multiple roles allowed
        return requiredRole.includes(authStore.role)
      }
      if (typeof requiredRole === 'string') {
        if (requiredRole === 'admin-manager') {
          // Only admin-manager can access
          return authStore.role === 'admin-manager'
        }
        if (requiredRole === 'content-manager') {
          // Both content-manager and admin-manager can access
          return authStore.role === 'content-manager' || authStore.role === 'admin-manager'
        }
        return authStore.role === requiredRole
      }
      return true
    }

    const hasAccess = to.matched
      .filter(record => record.meta.requiresRole)
      .every(record => roleAllowed(record.meta.requiresRole))

    if (!hasAccess) {
      next({ name: 'home' })
      return
    }

    // Check city selection requirements (for regular users only)
    // Content managers and admin managers use their resource center's city automatically
    if (authStore.role !== 'content-manager' && authStore.role !== 'admin-manager') {
      // Check if user has selected a city
      if (!authStore.selectedCityForDisplay && to.name !== 'city') {
        console.log('🏙️ Router guard: No city selected, redirecting to city selection')
        next({ name: 'city' })
        return
      }
    }
  }

  // Check if route requires guest (unauthenticated user)
  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (authStore.isLoggedIn) {
      // Redirect authenticated users away from auth pages
      const redirectTo = (to.query.redirect as string) || '/dashboard'
      next(redirectTo)
      return
    }
  }

  next()
})

export default router
